import { generateCollectionFilter, ServerSideState } from '@price-for-profit/data-grid';
import { DataAccessPaginatedResponse, getData, IDataAccessService, NoInfer } from '@price-for-profit/micro-services';
import { CLIENT_ID } from 'shared/constants';
import {
  CustomerCommodity,
  CustomerCommodityView,
  DropdownOption,
  ImportanceParameters,
  RevisedImportanceNamedQueryParameters,
  RevisedSegmentNamedQueryParameters,
  SegmentParameters,
} from 'shared/types';

const CUSTOMER_COMMODITY = 'vw_customer_commodity';
const SEGMENT_PROC = 'sp_update_segment';
const IMPORTANCE_PROC = 'sp_update_importance';

export interface UpdateSegmentProps extends SegmentParameters {}
export interface UpdateImportanceProps extends ImportanceParameters {}

export interface ICustomerCommodityService {
  getCommodities(): Promise<CustomerCommodity[]>;
  getCommoditiesForGrid(state: ServerSideState): Promise<DataAccessPaginatedResponse<CustomerCommodity>>;
  getCustomerCommodityOptions(optionType: string): Promise<DropdownOption[]>;
  updateSegment(props: UpdateSegmentProps): Promise<CustomerCommodity[]>;
  updateImportance(props: UpdateImportanceProps): Promise<CustomerCommodity[]>;
}

function customerCommodityViewToCustomerCommodity(sql: CustomerCommodityView): CustomerCommodity {
  return {
    ...sql,
    hierarchy: sql.hierarchy.split(','),
    segmentLastModified: sql.segmentLastModified ?? '',
    importanceLastModified: sql.importanceLastModified ?? '',
  };
}

export class CustomerCommodityService implements ICustomerCommodityService {
  constructor(private dataAccessService: IDataAccessService) {}
  async getCommoditiesForGrid(state: ServerSideState): Promise<DataAccessPaginatedResponse<CustomerCommodity>> {
    const { data, metadata } = await this.dataAccessService.getCollection<CustomerCommodityView, 'main'>({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      tableId: CUSTOMER_COMMODITY,
      page: state.pageNumber,
      pageSize: state.pageSize,
      sortBy: state.sortModel[0]?.field as NoInfer<keyof CustomerCommodityView>,
      sortDescending: state.sortModel[0]?.sort === 'desc',
      collectionFilter: generateCollectionFilter<CustomerCommodityView>(state.filterModel),
    });

    return {
      data: data.map<CustomerCommodity>(customerCommodityViewToCustomerCommodity),
      metadata,
    };
  }

  async updateSegment({
    masterCustomerCode,
    segment,
    userId,
    userEmail,
  }: UpdateSegmentProps): Promise<CustomerCommodity[]> {
    const { data } = await this.dataAccessService.executeNamedQuery<
      CustomerCommodityView,
      RevisedSegmentNamedQueryParameters,
      'main'
    >({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      queryName: SEGMENT_PROC,
      parameters: {
        SegmentParameters: [
          {
            masterCustomerCode,
            segment,
            userId,
            userEmail,
          },
        ],
      },
      partitionKey: '', // FIX This should be nullable.
    });

    return data.map(customerCommodityViewToCustomerCommodity);
  }

  async updateImportance({
    importance,
    commodityId,
    userId,
    masterCustomerCode,
    userEmail,
  }: UpdateImportanceProps): Promise<CustomerCommodity[]> {
    const { data } = await this.dataAccessService.executeNamedQuery<
      CustomerCommodityView,
      RevisedImportanceNamedQueryParameters,
      'main'
    >({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      queryName: IMPORTANCE_PROC,
      parameters: {
        ImportanceParameters: [
          {
            masterCustomerCode,
            commodityId,
            importance,
            userId,
            userEmail,
          },
        ],
      },
      partitionKey: '', // FIX This should be nullable.
    });

    return data.map(customerCommodityViewToCustomerCommodity);
  }

  async getCommodities(): Promise<CustomerCommodity[]> {
    const sql = await getData(page =>
      this.dataAccessService.getCollection<CustomerCommodityView, 'main'>({
        clientId: CLIENT_ID,
        databaseLabel: 'main',
        tableId: CUSTOMER_COMMODITY,
        pageSize: 1000,
        page,
      })
    );

    return sql.map<CustomerCommodity>(customerCommodityViewToCustomerCommodity);
  }

  async getCustomerCommodityOptions(optionType: string): Promise<DropdownOption[]> {
    const { data } = await this.dataAccessService.getCollection<DropdownOption, 'main'>({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      tableId: 'DropdownOption',
      pageSize: 100,
      sortBy: 'sort',
      collectionFilter: {
        property: 'type',
        operator: 'eq',
        value: optionType,
      },
    });

    return data;
  }
}
